import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import nocodeClient from '../lib/nocode';
import { exportDataToJSON, importDataFromJSON } from '../utils/backupUtils';
import { logger } from '../utils/logging';
import { sendTestEmail, getEmailSettings as fetchEmailSettings } from '../utils/emailUtils';
import * as FiIcons from 'react-icons/fi';

const {
  FiArrowLeft, FiSave, FiMail, FiKey, FiCheck, FiAlertTriangle,
  FiSettings, FiDownload, FiUpload, FiX, FiFile, FiZap, FiTrash2
} = FiIcons;

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreError, setRestoreError] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [logs, setLogs] = useState(logger.getLogs());
  const [debugLoading, setDebugLoading] = useState({ api: false, email: false });

  const [emailSettings, setEmailSettings] = useState({
    postmark_api_key: '',
    from_email: '',
    from_name: 'BPA Success Portal',
    email_enabled: false,
  });

  const [settingsId, setSettingsId] = useState(null);

  const isPeter = user?.name?.toLowerCase().includes('peter') || user?.username?.toLowerCase().includes('peter');

  useEffect(() => {
    if (isPeter) {
      loadEmailSettings();
    } else {
      setInitialLoading(false);
    }
  }, [isPeter]);

  useEffect(() => {
    const unsubscribe = logger.subscribe((logEntry) => {
      if (logEntry === null) {
        setLogs([]);
      } else {
        setLogs(prevLogs => [logEntry, ...prevLogs].slice(0, 100));
      }
    });
    return () => unsubscribe();
  }, []);

  const loadEmailSettings = async () => {
    setInitialLoading(true);
    try {
      const response = await nocodeClient.search('app_settings_bpa_5e7gf', { setting_name: 'email_config' });
      if (response.data && response.data.length > 0) {
        const settingsData = response.data[0];
        const parsedValue = JSON.parse(settingsData.setting_value);
        setEmailSettings({
          ...parsedValue,
          postmark_api_key: parsedValue.postmark_api_key ? '••••••••••••••••' : '',
        });
        setSettingsId(settingsData.id);
      } else {
        const envApiKey = import.meta.env.VITE_POSTMARK_API_KEY;
        if (envApiKey) {
          logger.info('SYSTEM', 'Populating settings page with environment variable fallbacks.');
          setEmailSettings({
            email_enabled: true,
            postmark_api_key: '••••••••••••••••',
            from_email: import.meta.env.VITE_POSTMARK_FROM_EMAIL || '',
            from_name: import.meta.env.VITE_POSTMARK_FROM_NAME || 'BPA Success Portal',
          });
        }
      }
    } catch (err) {
      console.error('Error loading email settings:', err);
      logger.error('SYSTEM', 'Failed to load email settings.', { error: err.message });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleEmailSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmailSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveEmailSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      let settingsToStore = { ...emailSettings };
      if (settingsToStore.postmark_api_key === '••••••••••••••••') {
        if (settingsId) {
          const response = await nocodeClient.read('app_settings_bpa_5e7gf', { id: settingsId });
          const existingSettings = JSON.parse(response.data.setting_value);
          settingsToStore.postmark_api_key = existingSettings.postmark_api_key;
        } else {
          settingsToStore.postmark_api_key = import.meta.env.VITE_POSTMARK_API_KEY;
        }
      }
      const payload = { setting_name: 'email_config', setting_value: JSON.stringify(settingsToStore) };
      if (settingsId) {
        await nocodeClient.update('app_settings_bpa_5e7gf', settingsId, payload);
      } else {
        const response = await nocodeClient.create('app_settings_bpa_5e7gf', payload);
        setSettingsId(response.id);
      }
      setSuccess('Email settings saved securely!');
      if (settingsToStore.postmark_api_key) {
        setEmailSettings(prev => ({ ...prev, postmark_api_key: '••••••••••••••••' }));
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save email settings');
      logger.error('SYSTEM', 'Failed to save email settings.', { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleBackupClick = () => exportDataToJSON();
  const handleRestoreClick = () => setShowRestorePrompt(true);
  const handleFileChange = (e) => { if (e.target.files && e.target.files[0]) { setSelectedFile(e.target.files[0]); setRestoreError(''); } };

  const handleRestoreSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) { setRestoreError('Please select a backup file.'); return; }
    setRestoreLoading(true); setRestoreError('');
    await importDataFromJSON(selectedFile);
    setRestoreLoading(false);
  };

  const handleTestApiConnection = async () => {
    setDebugLoading(prev => ({ ...prev, api: true }));
    logger.info('SYSTEM', 'Performing API connection test...');
    try { await nocodeClient.read('coaches_bpa_5e7gf', { _limit: 1 }); } catch (err) { /* Handled by logger */ } finally { setDebugLoading(prev => ({ ...prev, api: false })); }
  };

  const handleTestEmail = async () => {
    setDebugLoading(prev => ({ ...prev, email: true }));
    logger.info('SYSTEM', 'Performing email service test...');
    const currentSettings = await fetchEmailSettings();
    if (!currentSettings || !currentSettings.email_enabled) {
      logger.error('EMAIL', 'Test failed: Email functionality is not enabled or configured.');
      setDebugLoading(prev => ({ ...prev, email: false }));
      return;
    }
    try {
      if (!currentSettings.postmark_api_key) throw new Error("Postmark API key not configured.");
      const result = await sendTestEmail({ apiKey: currentSettings.postmark_api_key, fromEmail: currentSettings.from_email, fromName: currentSettings.from_name, toEmail: user.email, toName: user.name });
      if (result.success) logger.success('SYSTEM', 'Email test concluded successfully.');
      else logger.error('SYSTEM', 'Email test concluded with an error.');
    } catch (err) {
      logger.error('SYSTEM', `Email test failed: ${err.message}`);
    } finally {
      setDebugLoading(prev => ({ ...prev, email: false }));
    }
  };

  const getStatusColor = (status) => ({ SUCCESS: 'text-green-400', ERROR: 'text-red-400', INFO: 'text-blue-400' }[status] || 'text-gray-400');

  if (!isPeter) return <Navigate to="/" replace />;
  if (initialLoading) return <div className="p-6 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>;

  return (
    <div className="p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"><SafeIcon icon={FiArrowLeft} className="w-5 h-5" /><span>Back to Dashboard</span></Link>
        </div>
        <div className="flex items-center space-x-3"><SafeIcon icon={FiSettings} className="w-8 h-8 text-primary-600" /><div><h1 className="text-3xl font-bold text-gray-900">App Settings</h1><p className="text-gray-600">Configure global application settings (Peter access only)</p></div></div>
      </motion.div>

      {success && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center"><SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />{success}</motion.div>}
      {error && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center"><SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-2" />{error}</motion.div>}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Email Configuration</h2>
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"><div><label className="block text-sm font-medium text-gray-700">Enable Email Functionality</label><p className="text-xs text-gray-500 mt-1">Turn on email sending capabilities</p></div><label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" name="email_enabled" checked={emailSettings.email_enabled} onChange={handleEmailSettingsChange} className="sr-only peer" /><div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div></label></div>
          {emailSettings.email_enabled && (<>
            <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiKey} className="w-4 h-4 inline mr-2" /> Postmark Server API Key</label><input type="password" name="postmark_api_key" value={emailSettings.postmark_api_key} onChange={handleEmailSettingsChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder={emailSettings.postmark_api_key === '••••••••••••••••' ? 'API Key is configured' : 'Enter Postmark Server API Key'} /><p className="text-xs text-gray-500 mt-1">{emailSettings.postmark_api_key === '••••••••••••••••' ? 'Enter a new key to update it.' : 'Get your API key from Postmark.'}</p></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiMail} className="w-4 h-4 inline mr-2" /> From Email Address</label><input type="email" name="from_email" value={emailSettings.from_email} onChange={handleEmailSettingsChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="noreply@yourdomain.com" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">From Name</label><input type="text" name="from_name" value={emailSettings.from_name} onChange={handleEmailSettingsChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="BPA Success Portal" /></div>
            <div className="flex justify-end pt-6 border-t"><button onClick={saveEmailSettings} disabled={loading} className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">{loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><SafeIcon icon={FiSave} className="w-4 h-4" /><span>Save Email Settings</span></>}</button></div>
          </>)}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Data Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border rounded-lg p-4 flex flex-col"><div className="flex items-center mb-3"><SafeIcon icon={FiDownload} className="w-5 h-5 text-green-600 mr-2" /><h4 className="text-sm font-medium">Create Backup</h4></div><p className="text-sm text-gray-600 mb-4 flex-grow">Download a JSON backup of all data.</p><button onClick={handleBackupClick} className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"><SafeIcon icon={FiDownload} className="w-4 h-4" /><span>Download Backup</span></button></div>
          <div className="border rounded-lg p-4 flex flex-col"><div className="flex items-center mb-3"><SafeIcon icon={FiUpload} className="w-5 h-5 text-blue-600 mr-2" /><h4 className="text-sm font-medium">Restore Data</h4></div><p className="text-sm text-gray-600 mb-4 flex-grow">Restore data from a JSON backup.</p><button onClick={handleRestoreClick} className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><SafeIcon icon={FiUpload} className="w-4 h-4" /><span>Restore from Backup</span></button></div>
          <div className="border rounded-lg p-4 flex flex-col"><div className="flex items-center mb-3"><SafeIcon icon={FiFile} className="w-5 h-5 text-purple-600 mr-2" /><h4 className="text-sm font-medium">Convert Legacy Backups</h4></div><p className="text-sm text-gray-600 mb-4 flex-grow">Convert older CSV backups to JSON.</p><Link to="/csv-converter" className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"><SafeIcon icon={FiFile} className="w-4 h-4" /><span>Convert CSV Files</span></Link></div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Debugging & Logging</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="border rounded-lg p-4 flex flex-col items-center text-center"><h4 className="text-sm font-medium mb-2">API Connection</h4><p className="text-sm text-gray-600 mb-4 flex-grow">Verify connection to the API.</p><button onClick={handleTestApiConnection} disabled={debugLoading.api} className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">{debugLoading.api ? <div className="animate-spin rounded-full h-4 w-4 border-b-2"></div> : <><SafeIcon icon={FiZap} className="w-4 h-4" /><span>Run API Test</span></>}</button></div>
          <div className="border rounded-lg p-4 flex flex-col items-center text-center"><h4 className="text-sm font-medium mb-2">Email Service</h4><p className="text-sm text-gray-600 mb-4 flex-grow">Send a test email to your account.</p><button onClick={handleTestEmail} disabled={debugLoading.email} className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">{debugLoading.email ? <div className="animate-spin rounded-full h-4 w-4 border-b-2"></div> : <><SafeIcon icon={FiMail} className="w-4 h-4" /><span>Run Email Test</span></>}</button></div>
          <div className="border rounded-lg p-4 flex flex-col items-center text-center"><h4 className="text-sm font-medium mb-2">Clear Logs</h4><p className="text-sm text-gray-600 mb-4 flex-grow">Clear the current session's log history.</p><button onClick={() => logger.clear()} className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"><SafeIcon icon={FiTrash2} className="w-4 h-4" /><span>Clear Logs</span></button></div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Log Stream</h3>
          <div className="bg-gray-800 text-white font-mono text-xs rounded-lg p-4 h-96 overflow-y-auto">{logs.length > 0 ? logs.map((log, index) => (<div key={index} className="flex items-start mb-2 last:mb-0"><span className="text-gray-500 mr-2">{new Date(log.timestamp).toLocaleTimeString()}</span><span className={`font-bold mr-2 ${getStatusColor(log.status)}`}>[{log.status}]</span><span className="text-yellow-400 mr-2">[{log.type}]</span><p className="flex-1 whitespace-pre-wrap text-gray-200">{log.message} {Object.keys(log.details).length > 0 && <span className="text-gray-500">- {JSON.stringify(log.details)}</span>}</p></div>)) : <p className="text-gray-500">No logs yet. Perform an action to see logs here.</p>}</div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showRestorePrompt && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Restore from Backup</h3>
            <form onSubmit={handleRestoreSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium mb-2">Backup File (.json)</label><div className="mt-1 flex items-center"><input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="sr-only" id="file-upload" /><label htmlFor="file-upload" className="cursor-pointer px-4 py-2 border rounded-lg text-sm flex-grow truncate">{selectedFile ? selectedFile.name : 'Choose file'}</label>{selectedFile && <button type="button" onClick={() => { setSelectedFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} className="ml-2 p-1 text-gray-400"><SafeIcon icon={FiX} className="h-5 w-5" /></button>}</div></div>
              {restoreError && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{restoreError}</div>}
              <div className="bg-amber-50 text-amber-700 px-4 py-3 rounded-lg text-sm"><p className="font-medium">Warning:</p><p>Restoring will update existing data and archive new records.</p></div>
              <div className="flex justify-end space-x-3"><button type="button" onClick={() => { setShowRestorePrompt(false); setSelectedFile(null); setRestoreError(''); }} className="px-4 py-2 font-medium">Cancel</button><button type="submit" disabled={restoreLoading || !selectedFile} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2">{restoreLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2"></div><span>Restoring...</span></> : <><SafeIcon icon={FiUpload} className="w-4 h-4" /><span>Restore Data</span></>}</button></div>
            </form>
          </motion.div>
        </div>)}
      </AnimatePresence>
    </div>
  );
};

export default Settings;